import { faker } from '@faker-js/faker';
import { IClaimMessengerConversation } from 'app/claims/components/messenger/interfaces/claim-messenger-conversation.interface';
import { makeSyncStatus } from 'app/mocks/factories/claims/makes/make-sync-status';

export function makeClaimConversation(
    topic: string,
    overrides: Partial<IClaimMessengerConversation> = {},
): IClaimMessengerConversation {
    const user = faker.person.fullName();
    const adjuster = faker.person.fullName();

    let totalCount = overrides.totalCount ?? faker.number.int({ min: 6, max: 12 });

    // Ensure last message ends with the user by making totalCount odd
    if (totalCount % 2 === 0) {
        totalCount += 1;
    }

    const messagesPerDay = Math.ceil(totalCount / 3);

    const now = new Date();
    const yesterday = new Date(now);
    const twoDaysAgo = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    twoDaysAgo.setDate(now.getDate() - 2);

    const dayBuckets = [twoDaysAgo, yesterday, now];

    const messages = Array.from({ length: totalCount })
                          .map((_, index) => {
                              const isUser = index % 2 === 0;
                              const author = isUser ? user : adjuster;

                              const day = dayBuckets[Math.floor(index / messagesPerDay)] ?? now;
                              const time = new Date(day);
                              time.setHours(faker.number.int({ min: 8, max: 17 }));
                              time.setMinutes(faker.number.int({ min: 0, max: 59 }));

                              return {
                                  messageId: faker.string.uuid(),
                                  author,
                                  body: faker.lorem.sentences(faker.number.int({ min: 1, max: 2 })),
                                  time: clampToNow(time),
                                  isAdjuster: !isUser,
                                  isAutoMessage: false,
                                  isMessageRead: true, // default; patched for last one
                                  isSystemMessage: false,
                                  isStale: false,
                                  topic,
                              };
                          })
                          .sort((a, b) => a.time.localeCompare(b.time));

    // Patch the last message to be unread
    const lastIndex = messages.length - 1;
    const last = messages[lastIndex];
    last.isMessageRead = false;

    return {
        claimId: overrides.claimId ?? faker.number.int({ min: 1000, max: 9999 }),
        claimNumber: overrides.claimNumber ?? [
            faker.number.int({ min: 0, max: 999 }).toString().padStart(3, '0'),
            faker.number.int({ min: 0, max: 99 }).toString().padStart(2, '0'),
            faker.number.int({ min: 0, max: 999999 }).toString().padStart(6, '0'),
        ].join('-'),
        messages,
        totalCount,
        unreadAdjusterCount: overrides.unreadAdjusterCount ?? 0,
        syncStatus: overrides.syncStatus ?? makeSyncStatus(),
        ...overrides,
    };
}

function clampToNow(date: Date): string {
    return date.toISOString() > new Date().toISOString()
        ? new Date().toISOString()
        : date.toISOString();
}
